import React, { Suspense, useEffect, useState, useMemo } from 'react'
import moment from 'moment'

import Grid from '@material-ui/core/Grid'
import { GridItem } from 'Components/Page/StatsPageWrapper'
import Loader from '../../../HighchartsCharts/components/Loader'
import ChartHeader from '../ChartHeader'
import {
  formatScansData,
  getDiffBetweenTwoDays,
  renderScanUniqueGraphPopup,
} from './utils'
import { getIsPositive, getPastDates, getPeriodDiff } from '../utils'
import { OPTIONS_FILTERS_ENUM_TYPES, selectOptions } from '../options'
import { chartOptions } from './options'

const Chart = React.lazy(() => import('Components/Chart'))

const ScanUniqueGraph = ({ scansData, dateNow }) => {
  const [options, setOptions] = useState(chartOptions)
  const [filteredScanData, setFilteredScanData] = useState({
    scans: [],
    unique: {},
    amountScans: 0,
    amountUniqueScans: 0,
  })
  const [previousFilteredScanData, setPreviousFilteredScanData] = useState({
    amountScans: 0,
    amountUniqueScans: 0,
  })
  const [selectedFilter, setSelectedFilter] = useState(
    OPTIONS_FILTERS_ENUM_TYPES.LAST_7_DAYS,
  )
  const [previousDate, setPreviousDate] = useState(
    getPastDates(dateNow, selectedFilter),
  )

  const { scans, unique, amountScans, amountUniqueScans } = filteredScanData

  useEffect(() => {
    setPreviousDate(getPastDates(dateNow, selectedFilter))
  }, [selectedFilter])

  useEffect(() => {
    const actualScanData = scansData.filter(
      item =>
        +item.createdAt > previousDate && +item.createdAt < dateNow.valueOf(),
    )

    const formatActualData = formatScansData({
      data: actualScanData.length
        ? actualScanData
        : [
            {
              createdAt: previousDate,
              flyerCode: null,
            },
          ],
      today: dateNow.valueOf(),
      endFilterDay: previousDate,
    })
    setFilteredScanData(formatActualData)

    const startPreviousPreviousDate = getPastDates(previousDate, selectedFilter)
    const previousPreviousData = scansData.filter(
      item =>
        +item.createdAt > startPreviousPreviousDate &&
        +item.createdAt < previousDate.valueOf(),
    )
    const formatPreviousScanData = formatScansData({
      data: previousPreviousData,
    })
    setPreviousFilteredScanData(formatPreviousScanData)
  }, [previousDate])

  useEffect(() => {
    setOptions(prev => ({
      ...prev,
      xAxis: {
        categories: Object.keys(scans),
      },
      series: [
        {
          name: 'Scans',
          data: Object.values(scans).map(array => array.length),
          color: '#273754',
          marker: {
            symbol: 'square',
          },
        },
        {
          name: 'Unique',
          data: Object.values(unique).map(array => array.length),
          dashStyle: 'Dash',
          color: '#273754',
          marker: {
            symbol: 'diamond',
          },
        },
      ],
      tooltip: {
        useHTML: true,
        shared: true,
        formatter: function() {
          const [scansAmount, uniqueAmount] = this.points
          const arrKeys = Object.keys(unique)
          const previousKey = arrKeys[arrKeys.indexOf(this.x) - 1]

          const previousDayUniqueScans = previousKey
            ? unique[previousKey].length
            : null
          const previousDayAllScans = previousKey
            ? scans[previousKey].length
            : null

          const currentDay = moment(uniqueAmount.x).format('MMM DD')
          const nextDay = moment(previousKey).format('MMM DD')

          const diffUnique = getDiffBetweenTwoDays(
            uniqueAmount.y,
            previousDayUniqueScans,
          )
          const diffScans = getDiffBetweenTwoDays(
            scansAmount.y,
            previousDayAllScans,
          )
          const isPositiveUnique = getIsPositive(diffUnique)
          const isPositiveScans = getIsPositive(diffScans)
          return renderScanUniqueGraphPopup({
            previousKey,
            currentDay,
            nextDay,
            scansAmount: scansAmount.y,
            uniqueAmount: uniqueAmount.y,
            isPositiveScans,
            diffScans,
            isPositiveUnique,
            diffUnique,
          })
        },
      },
    }))
  }, [filteredScanData])

  const amountDiff = getPeriodDiff(
    filteredScanData.amountScans,
    previousFilteredScanData.amountScans,
  )
  const uniqueDiff = getPeriodDiff(
    filteredScanData.amountUniqueScans,
    previousFilteredScanData.amountUniqueScans,
  )

  const statsOptions = useMemo(
    () => [
      {
        title: 'Scans',
        value: amountScans,
        isPositive: getIsPositive(amountDiff),
        diff: Math.abs(amountDiff),
      },
      {
        title: 'Unique Scans',
        value: amountUniqueScans,
        isPositive: getIsPositive(uniqueDiff),
        diff: Math.abs(uniqueDiff),
      },
    ],
    [amountScans, amountDiff, amountUniqueScans, uniqueDiff],
  )

  const handleFormChange = item => {
    if (item.isDirty) {
      setSelectedFilter(item.value)
    }
  }

  return (
    <div>
      <GridItem>
        <Suspense fallback={<Loader />}>
          <Grid container>
            <Grid item xs={12}>
              <ChartHeader
                statsOptions={statsOptions}
                selectOptions={selectOptions}
                handleFormChange={handleFormChange}
                selectedValue={selectedFilter}
              />
            </Grid>
            <Grid item xs={12}>
              <Chart options={options} />
            </Grid>
          </Grid>
        </Suspense>
      </GridItem>
    </div>
  )
}

export default ScanUniqueGraph



// WEBPACK FOOTER //
// ./src/Containers/StatsOverview/components/CampaignPerforming/Charts/ScanUniqueGraph/index.js